import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PlatformIcon } from '../components/PlatformIcon';
import { StatusBadge } from '../components/StatusBadge';
import { ChartIcon } from '../components/icons/NavigationIcons';

export const CampaignDetailPage: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');

  // Mock campaign data
  const campaign = {
    id: campaignId || '1',
    name: 'Summer Sale 2026 - Premium Collection',
    platform: 'meta' as const,
    status: 'active' as const,
    budget: 5000,
    spent: 3420,
    impressions: 245000,
    clicks: 8950,
    conversions: 312,
    revenue: 12450,
    ctr: 3.65,
    cpc: 0.38,
    cpa: 10.96,
    roas: 3.64,
    created: '2026-01-15',
    lastUpdated: '2 hours ago',
  };

  const performanceData = [
    { date: 'Jan 20', spend: 450, revenue: 1620, conversions: 42 },
    { date: 'Jan 21', spend: 520, revenue: 1840, conversions: 48 },
    { date: 'Jan 22', spend: 480, revenue: 1710, conversions: 45 },
    { date: 'Jan 23', spend: 510, revenue: 1890, conversions: 51 },
    { date: 'Jan 24', spend: 490, revenue: 1750, conversions: 46 },
    { date: 'Jan 25', spend: 530, revenue: 1980, conversions: 53 },
    { date: 'Jan 26', spend: 440, revenue: 1660, conversions: 37 },
  ];

  const creatives = [
    { id: 1, name: 'Summer Vibes A', format: 'Image', ctr: 4.2, conversions: 89 },
    { id: 2, name: 'Summer Vibes B', format: 'Video', ctr: 5.1, conversions: 124 },
    { id: 3, name: 'Beach Collection', format: 'Carousel', ctr: 3.8, conversions: 99 },
  ];

  const activities = [
    { time: '2 hours ago', action: 'Budget increased', user: 'John Doe', details: 'From $4,500 to $5,000' },
    { time: '1 day ago', action: 'Creative added', user: 'Jane Smith', details: 'Summer Vibes B (Video)' },
    { time: '2 days ago', action: 'Targeting updated', user: 'John Doe', details: 'Added LA, San Diego' },
    { time: '3 days ago', action: 'Campaign activated', user: 'John Doe', details: 'Status changed to Active' },
  ];

  const maxRevenue = Math.max(...performanceData.map(d => d.revenue));

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate('/campaigns')}
        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Campaigns
      </button>

      {/* Campaign Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-start gap-4">
            <PlatformIcon platform={campaign.platform} />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{campaign.name}</h1>
              <div className="flex items-center gap-3 mt-2">
                <StatusBadge status={campaign.status} />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Created {campaign.created}
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Updated {campaign.lastUpdated}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
              Edit
            </button>
            <button className="px-4 py-2 bg-gp-orange text-white rounded-lg hover:bg-gp-orange-600">
              Pause Campaign
            </button>
          </div>
        </div>
      </div>

      {/* Time Range Selector */}
      <div className="flex justify-end gap-2">
        {(['7d', '30d', '90d'] as const).map((range) => (
          <button
            key={range}
            onClick={() => setTimeRange(range)}
            className={`
              px-4 py-2 rounded-lg text-sm font-medium transition-colors
              ${
                timeRange === range
                  ? 'bg-gp-sungold text-white'
                  : 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }
            `}
          >
            Last {range.replace('d', ' Days')}
          </button>
        ))}
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">ROAS</p>
          <p className="text-3xl font-bold text-gp-sungold mt-1">{campaign.roas.toFixed(2)}x</p>
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">↑ 18% vs last period</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">Revenue</p>
          <p className="text-3xl font-bold text-gp-forest dark:text-gp-forest-400 mt-1">
            ${(campaign.revenue / 1000).toFixed(1)}k
          </p>
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">↑ 24% vs last period</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">Conversions</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{campaign.conversions}</p>
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">↑ 32% vs last period</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">CTR</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{campaign.ctr}%</p>
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">↑ 12% vs last period</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Performance Chart */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Performance Trend</h2>
            <div className="flex gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gp-sungold"></div>
                <span className="text-gray-600 dark:text-gray-400">Spend</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gp-forest"></div>
                <span className="text-gray-600 dark:text-gray-400">Revenue</span>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            {performanceData.map((day, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xs text-gray-600 dark:text-gray-400 w-16">{day.date}</span>
                <div className="flex-1 flex gap-1">
                  <div
                    className="bg-gp-sungold-200 dark:bg-gp-sungold-800 h-8 rounded flex items-center justify-end px-2"
                    style={{ width: `${(day.spend / 600) * 100}%` }}
                  >
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      ${day.spend}
                    </span>
                  </div>
                  <div
                    className="bg-gp-forest-200 dark:bg-gp-forest-800 h-8 rounded flex items-center justify-end px-2"
                    style={{ width: `${(day.revenue / maxRevenue) * 100}%` }}
                  >
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      ${day.revenue}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Budget Status */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Budget</h2>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600 dark:text-gray-400">Spent</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  ${campaign.spent.toLocaleString()} / ${campaign.budget.toLocaleString()}
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                <div
                  className="bg-gp-sungold h-3 rounded-full"
                  style={{ width: `${(campaign.spent / campaign.budget) * 100}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                {((campaign.spent / campaign.budget) * 100).toFixed(1)}% of budget used
              </p>
            </div>
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Daily avg</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  ${(campaign.spent / 12).toFixed(0)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Remaining</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  ${(campaign.budget - campaign.spent).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Est. depletion</span>
                <span className="font-medium text-gray-900 dark:text-white">5 days</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Ad Creatives */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Ad Creatives</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-900">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Creative
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Format
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  CTR
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Conversions
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {creatives.map((creative) => (
                <tr key={creative.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {creative.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                    {creative.format}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                    {creative.ctr}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                    {creative.conversions}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <button className="text-gp-sungold hover:text-gp-sungold-600">Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Activity Log */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Activity Log</h2>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {activities.map((activity, index) => (
              <div key={index} className="flex gap-4">
                <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-gp-sungold"></div>
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {activity.action}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {activity.details}
                      </p>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{activity.time}</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">by {activity.user}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignDetailPage;
